import type { InstrumentId } from "@trader/shared/MarketData"
import { useEffect, useSyncExternalStore } from "react"
import { documentSaver, loadDocument } from "../../documents.ts"
import { toast } from "../../ui/toasts.ts"
import { isTypingIn } from "../../ui/typing.ts"
import {
  addSection,
  addSymbol,
  decodeWatchlist,
  MAX_SYMBOLS,
  DEFAULT_WATCHLIST,
  moveEntry,
  removeSection,
  removeSymbol,
  renameSection,
  symbolsOf,
  toggleSection,
  type Watchlist
} from "./watchlistModel.ts"

// The user's watchlist (docs/design/0014): saved on the server ("watchlist" document) so it follows
// them across browsers; this browser keeps a copy, per user, so it shows at once.

const SERVER_KEY = "watchlist"
const localKey = (userId: string) => `trader.watchlist.v1.${userId}`

let userId: string | null = null
let state: Watchlist = DEFAULT_WATCHLIST
/** Counts the user's edits: the server's copy, arriving late, mustn't undo one made meanwhile. */
let edits = 0
const listeners = new Set<() => void>()
const saver = documentSaver(800)

const setState = (next: Watchlist, save: boolean) => {
  state = next
  if (userId !== null) {
    try {
      localStorage.setItem(localKey(userId), JSON.stringify(next))
    } catch {
      // Not cached here; the server copy still is.
    }
  }
  if (save) {
    edits++
    saver.schedule(SERVER_KEY, () => JSON.stringify(state))
  }
  for (const listener of listeners) listener()
}

export const watchlist = {
  /** After sign-in: this browser's copy at once, then the server's (which wins). */
  signedIn: async (id: string): Promise<void> => {
    if (userId !== null && userId !== id) saver.discard()
    userId = id
    let local: Watchlist | null = null
    try {
      local = decodeWatchlist(localStorage.getItem(localKey(id)))
    } catch {
      local = null
    }
    setState(local ?? DEFAULT_WATCHLIST, false)
    const before = edits
    const saved = decodeWatchlist(await loadDocument(SERVER_KEY).catch(() => null))
    // The server's copy wins, unless the user changed the list while it loaded (that edit is saved).
    if (userId === id && saved !== null && edits === before) setState(saved, false)
  },
  get: (): Watchlist => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  addSymbol: (id: InstrumentId, sectionId: string | null = null) => setState(addSymbol(state, id, sectionId), true),
  removeSymbol: (id: InstrumentId) => setState(removeSymbol(state, id), true),
  addSection: (title: string) => setState(addSection(state, crypto.randomUUID(), title), true),
  renameSection: (id: string, title: string) => setState(renameSection(state, id, title), true),
  toggleSection: (id: string) => setState(toggleSection(state, id), true),
  removeSection: (id: string) => setState(removeSection(state, id), true),
  move: (from: number, to: number) => setState(moveEntry(state, from, to), true),
  /** Sends a pending save now (before signing out). */
  flush: (): Promise<void> => saver.flush()
}

export const useWatchlist = (): Watchlist => useSyncExternalStore(watchlist.subscribe, watchlist.get)

// --- whether the panel is open (this browser) -------------------------------------------------------

const OPEN_KEY = "trader.watchlist.open"
let open = (() => {
  try {
    return localStorage.getItem(OPEN_KEY) !== "false"
  } catch {
    return true
  }
})()
const openListeners = new Set<() => void>()

export const watchlistPanel = {
  isOpen: (): boolean => open,
  set: (next: boolean): void => {
    open = next
    try {
      localStorage.setItem(OPEN_KEY, String(next))
    } catch {
      // Not remembered.
    }
    for (const listener of openListeners) listener()
  },
  subscribe: (listener: () => void) => {
    openListeners.add(listener)
    return () => {
      openListeners.delete(listener)
    }
  }
}

export const useWatchlistOpen = (): boolean => useSyncExternalStore(watchlistPanel.subscribe, watchlistPanel.isOpen)

// --- Alt+W: the active chart's symbol into the watchlist (TradingView's shortcut) ----------------------

/** Adds a symbol from the shortcut, opening the panel, and says what happened. */
export const addFromShortcut = (id: InstrumentId): void => {
  const before = watchlist.get()
  watchlistPanel.set(true)
  if (symbolsOf(before).includes(id)) {
    toast("info", "Already in the watchlist", id)
    return
  }
  if (symbolsOf(before).length >= MAX_SYMBOLS) {
    toast("error", "The watchlist is full", `It holds at most ${MAX_SYMBOLS} symbols; remove one first.`)
    return
  }
  watchlist.addSymbol(id)
  toast("ok", "Added to the watchlist", id)
}

/**
 * Alt+W adds `activeId` (the active chart's symbol), as on TradingView. Matched by the key's
 * position, since Alt+W types a character on some layouts (∑ on a Mac); not while typing in a field.
 */
export const useAddToWatchlistShortcut = (activeId: InstrumentId | null): void => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.code !== "KeyW") return
      if (isTypingIn(document.activeElement) || document.querySelector('[aria-modal="true"]') !== null || activeId === null) return
      event.preventDefault()
      addFromShortcut(activeId)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [activeId])
}
